import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import en from "./en.json";

// Only English (the fallback) ships in the main bundle; other languages load on demand.
const LOADERS = {
  it: () => import("./it.json"), es: () => import("./es.json"), fr: () => import("./fr.json"), de: () => import("./de.json"),
  pt: () => import("./pt.json"), nl: () => import("./nl.json"), pl: () => import("./pl.json"), tr: () => import("./tr.json"),
  ja: () => import("./ja.json"), zh: () => import("./zh.json"), ar: () => import("./ar.json"),
};
const DICTS = { en };
const CODES = ["en", ...Object.keys(LOADERS)];
const loadDict = (code) => (DICTS[code] ? Promise.resolve() : LOADERS[code]().then((m) => { DICTS[code] = m.default; }));
export const LANGUAGES = [
  ["en", "English"], ["it", "Italiano"], ["es", "Español"], ["fr", "Français"], ["de", "Deutsch"], ["pt", "Português"],
  ["nl", "Nederlands"], ["pl", "Polski"], ["tr", "Türkçe"], ["ja", "日本語"], ["zh", "中文"], ["ar", "العربية"],
];
const LOCALES = { zh: "zh-CN", pt: "pt-BR" };
const STORAGE_KEY = "am_lang";
const detect = () => {
  const urlLang = window.location.pathname.split("/")[1];
  if (CODES.includes(urlLang)) return urlLang;
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved && CODES.includes(saved)) return saved;
  const code = (navigator.language || "en").slice(0, 2).toLowerCase();
  return CODES.includes(code) ? code : "en";
};
export const i18nReady = loadDict(detect()).catch(() => {});

const lookup = (dict, key) => key.split(".").reduce((o, k) => (o && o[k] !== undefined ? o[k] : undefined), dict)
  ?? (key.startsWith("findings.") ? dict.findings?.[key.slice(9).replace(/\.(title|detail)$/, "")]?.[key.split(".").pop()] : undefined);

const I18nContext = createContext(null);

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(detect);
  const [ready, setReady] = useState(() => !!DICTS[lang]);
  const locale = LOCALES[lang] || lang;

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    document.querySelector('link[rel="manifest"]')?.setAttribute("href", lang === "en" ? "/manifest.webmanifest" : `/manifests/${lang}.webmanifest`);
  }, [lang]);

  useEffect(() => { if (!DICTS[lang]) loadDict(lang).finally(() => setReady(true)); }, [lang]);

  const setLang = useCallback((code, persist = true) => {
    if (!CODES.includes(code)) return;
    if (persist) localStorage.setItem(STORAGE_KEY, code);
    loadDict(code).catch(() => {}).finally(() => setLangState(code));
  }, []);

  const value = useMemo(() => {
    const nf = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 });
    const fmtNum = (n, digits) => (n == null ? "—" : new Intl.NumberFormat(locale, digits == null ? { maximumFractionDigits: 2 }
      : { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(n));
    const t = (key, vars = {}) => {
      const plural = typeof vars.count === "number" ? `${key}_${new Intl.PluralRules(locale).select(vars.count)}` : null;
      const d = DICTS[lang] || en;
      const raw = (plural && (lookup(d, plural) ?? lookup(en, plural))) ?? lookup(d, key) ?? lookup(en, key) ?? key;
      if (typeof raw !== "string") return key;
      return raw.replace(/\{\{(\w+)\}\}/g, (_, k) => (typeof vars[k] === "number" ? nf.format(vars[k]) : vars[k] ?? ""));
    };
    const fmtDate = (iso, withTime = true) => (iso ? new Intl.DateTimeFormat(locale, withTime
      ? { dateStyle: "medium", timeStyle: "short" } : { dateStyle: "medium" }).format(new Date(iso)) : "—");
    const languageName = (code) => { try { return new Intl.DisplayNames([locale], { type: "language" }).of(code); } catch { return code; } };
    return { lang, setLang, locale, t, fmtNum, fmtDate, languageName, rtl: lang === "ar" };
  }, [lang, locale, setLang, ready]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!ready) return null;
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);
